import type { PowerPathMoreTab } from '@ps/web-martech-cms-types/PowerPathMoreTab';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { CMSBrightcoveVideo, CMSHTML, CMSImage, CMSLink, useCMSReference } from '@swf/cms';
import { select } from '@swf/core/store';

import LearnMoreIcon from '../../assets/icons/learn-more.svg';
import { usePokerPowerPathWidget } from '../../model';

import { TXTCLI_Something_went_wrongX_please_t_ELL } from './More.messages.json';
import styles from './More.scss';

const More: React.FC = () => {
  const { widgetConfig } = usePokerPowerPathWidget();
  const moreTab = useCMSReference<PowerPathMoreTab>(widgetConfig.pp_widget_more_tab[0]);
  const origin = useSelector(select.location.getOrigin);
  const localePrefix = useSelector(select.location.getLocaleURL);
  const isEmbedded = useSelector(select.device.isEmbedded);

  if (!moreTab) {
    return (
      <div data-testid="empty-container" className={styles.emptyContainer}>
        <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />
      </div>
    );
  }
  const { blog_showcase: blogShowcase, brightcove_video: blogVideo } = moreTab;
  const embeddedHref = new URL(
    `external-${origin}${localePrefix}${blogShowcase.blog_link?.href}`
  ).toString();

  return (
    <div className={styles.moreContainer} data-testid="more-container">
      <div className={styles.section}>
        {blogVideo && (
          <div className={styles.videoContainer} data-testid="video-container">
            <CMSBrightcoveVideo
              video_id={blogVideo.brightcove_video_id}
              player_id={blogVideo.brightcove_player_id}
            />
          </div>
        )}
        <div className={styles.contentContainer}>
          <h1 className={styles.header}>{moreTab?.heading}</h1>
          <CMSHTML content={moreTab?.lobby_description} />
        </div>
      </div>
      {blogShowcase && (
        <>
          <h3>{blogShowcase.blog_title}</h3>
          <div className={classNames(styles.section, styles.secondarySection)}>
            <CMSImage asset={blogShowcase.showcase_image} alt={blogShowcase.blog_title} />
            <div className={styles.contentContainer}>
              <CMSHTML content={blogShowcase.blog_description} />
              <CMSLink
                url={isEmbedded ? embeddedHref : blogShowcase.blog_link?.href}
                target="_blank"
                title={blogShowcase.blog_link?.title}
                data-testid="cms-link"
              >
                <span className={styles.linkContainer}>
                  {blogShowcase.blog_link?.title} <LearnMoreIcon />
                </span>
              </CMSLink>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default More;
